import { defineStore } from "pinia";
import type { GridStackWidget } from "gridstack";
import { FrontpageWidget } from "~/helpers/frontpage-helpers";
import { useMutation } from "@vue/apollo-composable";
import { UpsertPreferencesDocument } from "~/graphql/generated/graphql";

export enum WidgetType {
  Portfolio = "Portfolio",
  OpexTotal = "OpexTotal",
  OpexList = "OpexList",
  Count = "Count",
  AccountsRealized = "AccountsRealized",
  AccountsLatestRealized = "AccountsLatestRealized",
  RerentalList = "RerentalList",
  Rent = "Rent",
  CustomText = "CustomText",
}

export interface Widget extends GridStackWidget {
  type: WidgetType;
  configuration: any;
}

export const widgetPortfolioDefault = (portfolioId: string) => ({
  type: WidgetType.Portfolio,
  w: 4,
  h: 9,
  minW: 4,
  minH: 9,
  configuration: { portfolioId },
  id: `widget_${generateUUID()}`,
});

export const widgetOpexListDefault = (config = FrontpageWidget.Opex.getListDefault()): Widget => ({
  type: WidgetType.OpexList,
  w: 6,
  h: 6,
  minW: 3,
  minH: 5,
  id: `widget_${generateUUID()}`,
  configuration: config,
});

export const widgetOpexTotalDefault = (config = FrontpageWidget.Opex.getTotalDefault()): Widget => ({
  type: WidgetType.OpexTotal,
  w: 6,
  h: 4,
  minW: 3,
  minH: 2,
  configuration: config,
  id: `widget_${generateUUID()}`,
});

export const widgetCountDefault = (type: FrontpageWidget.Count.Config["type"]) => ({
  type: WidgetType.Count,
  w: 4,
  h: 4,
  minW: 3,
  minH: 3,
  configuration: { type },
  id: `widget_${generateUUID()}`,
});

export const widgetAccountsRealizedDefault = (config = FrontpageWidget.Accounts.getRealizedDefault()): Widget => ({
  type: WidgetType.AccountsRealized,
  w: 10,
  h: 8,
  minW: 6,
  minH: 8,
  id: `widget_${generateUUID()}`,
  configuration: config,
});

export const widgetAccountsLatestRealizedDefault = (config = FrontpageWidget.Accounts.getLatestRealizedDefault()): Widget => ({
  type: WidgetType.AccountsLatestRealized,
  w: 3,
  h: 3,
  minW: 3,
  minH: 2,
  configuration: config,
  id: `widget_${generateUUID()}`,
});

const widgetRerentalListDefault = (): Widget => ({
  type: WidgetType.RerentalList,
  w: 6,
  h: 11,
  minW: 6,
  minH: 8,
  configuration: {},
  id: `widget_${generateUUID()}`,
});

export const widgetPlannedRentDefault = (config = FrontpageWidget.Rent.getDefault()): Widget => ({
  type: WidgetType.Rent,
  w: 6,
  h: 6,
  minW: 3,
  minH: 5,
  id: `widget_${generateUUID()}`,
  configuration: config,
});

export const widgetCustomTextDefault = (text: string): Widget => ({
  type: WidgetType.CustomText,
  w: 12,
  h: 1,
  minW: 1,
  minH: 1,
  id: `widget_${generateUUID()}`,
  configuration: {
    text,
  },
});

export function getDefaultWidget(type: WidgetType) {
  switch (type) {
    case WidgetType.Count:
      return widgetCountDefault("tenancy");
    case WidgetType.OpexList:
      return widgetOpexListDefault();
    case WidgetType.OpexTotal:
      return widgetOpexTotalDefault();
    case WidgetType.AccountsLatestRealized:
      return widgetAccountsLatestRealizedDefault();
    case WidgetType.AccountsRealized:
      return widgetAccountsRealizedDefault();
    case WidgetType.RerentalList:
      return widgetRerentalListDefault();
    case WidgetType.Rent:
      return widgetPlannedRentDefault();
    default:
      throw new Error(`Unsupported widget type: ${type}`);
  }
}

export function generateUUID() {
  return crypto.randomUUID();
}

export const useFrontpageStore = defineStore("frontpage", () => {
  const preferencesQuery = usePreferencesNamespace("frontpage");

  const preferences = computed(() => preferencesQuery.result.value);

  const widgetsLoading = computed(() => preferencesQuery.loading.value);

  const _widgets = ref<Widget[]>();

  watch(
    () => preferences.value?.widgets,
    (widgets) => {
      if (widgets) _widgets.value = widgets;
    }
  );

  const widgets = computed(() => _widgets.value);

  const setWidgets = function (w: Widget[]) {
    if (!preferences.value) return;

    preferencesQuery.update({ ...preferences.value, widgets: w });
  };

  return {
    widgets,
    setWidgets,
    widgetsLoading,
  };
});
